import { trans } from "i18n";

export const isIpad = Boolean(navigator.userAgent.match(/iPad/i) || ((navigator.platform === "MacIntel" || navigator.userAgent.match(/Macintosh/i)) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2));
export const isIphone = Boolean(navigator.userAgent.match(/iPhone/i));
export const isAndroid = Boolean(navigator.userAgent.match(/Android/i));
export const isMobile = isIpad || isIphone || isAndroid;

export const getErrorMessage = (str) => {
	const errorMsg = {
		"access denied": trans.gettext("You are not authorized for this request!"),
		"can't rate future bookings": trans.gettext("You cannot rate an event ahead of schedule."),
		"can't cancel past bookings": trans.gettext("You can only cancel future bookings."),
		"invalid credentials": trans.gettext("Wrong username or password."),
		"provider busy": trans.gettext("The selected time slot is not available, please select another time slot."),
		"too late for rescheduling": trans.gettext("It is too late to reschedule this booking."),
		"unauthorized": trans.gettext("This request requires login."),
		"invalid token": trans.gettext("Incorrect verification code."),
		"user already in other org": trans.gettext("Your user account is already associated with another organization."),
		"booking has expired": trans.gettext("This booking event has expired."),
		"booking is canceled": trans.gettext("This booking was canceled."),
		"invitation already accepted": trans.gettext("You have already accepted this invitation."),
		"maximum 3 guests allowed": trans.gettext("This event can have a maximum of 3 guests."),
		"userpack exhausted": trans.gettext("You have no remaining package credit for this service."),
		"service has pending userpack credits": trans.gettext("Service cannot be deleted while clients have pending package credits."),
		// Stripe
		"customer_tax_location_invalid": trans.gettext("Your organization address cannot be used to accurately determine tax rates. Verify that address fields such as zip code, state, or province have been added correctly.")
	};

	return str in errorMsg ? errorMsg[str] : str;
};

export const getPlatform = () => {
	const osMap = {
		"iPhone": "iOS",
		"Linux armv81": "Android",
		"Linux x86_64": "Linux",
		"MacIntel": "macOS",
		"Win32": "Windows"
	};

	if (navigator.userAgentData && navigator.userAgentData.platform)
		return navigator.userAgentData.platform;
	else if (isIpad)
		return "iPadOS";
	else if (navigator.platform && osMap[navigator.platform])
		return osMap[navigator.platform];
	else if (isAndroid)
		return "Android";
	else if (isIphone)
		return "iOS";
	else if ((/Win/i).test(navigator.userAgent))
		return "Windows";
	else if ((/Macintosh/i).test(navigator.userAgent))
		return "macOS";
	else if ((/Linux/i).test(navigator.userAgent))
		return "Linux";

	return "unknown";
};

export const getBrowser = () => {
	const ua = navigator.userAgent;

	// order is important, eg. Chrome on macOS includes Safari in userAgent string
	if (navigator.brave)
		return "brave";
	else if (ua.match(/edg/i))
		return "edge";
	else if (ua.match(/opr\//i))
		return "opera";
	else if (ua.match(/firefox|fxios/i))
		return "firefox";
	else if (ua.match(/chrome|chromium|crios/i))
		return "chrome";
	else if (ua.match(/safari/i))
		return "safari";

	return false;
};

export const getOSSettingsUrl = () => {
	switch (getPlatform()) {

	case "macOS":
		if (getBrowser() === "safari")
			return null;

		return {
			cam: "x-apple.systempreferences:com.apple.preference.security?Privacy_Camera",
			mic: "x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone"
		};

	case "Windows":
		return {
			cam: "ms-settings:privacy-webcam",
			mic: "ms-settings:privacy-microphone"
		};

	default:
		return null;
	}
};

export const getBrowserHelpUrl = () => {
	const browser = getBrowser();

	switch (browser) {

	case "brave":
		return "https://support.brave.com/hc/articles/360018205431-How-do-I-change-site-permissions-";

	case "chrome":
		return "https://support.google.com/chrome/answer/2693767";

	case "edge":
		return "https://support.microsoft.com/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857";

	case "firefox":
		return "https://support.mozilla.org/kb/how-manage-your-camera-and-microphone-permissions";

	case "opera":
		return "https://help.opera.com/opera36/set-web-preferences/#manageCamera";

	case "safari":
		if (isIphone)
			return "https://support.apple.com/guide/iphone/iph168c4bbd5/ios";
		else if (isIpad)
			return "https://support.apple.com/guide/ipad/ipade3312ebe/ipados";

		return "https://support.apple.com/guide/safari/ibrwe2159f50/mac";

	default:
		return false;
	}
};

export const sortByKey = (arr, ...keys) => {
	const fields = [];

	for (const key of keys)
		fields.push(key[0] === "^" ? { key: key.substring(1), asc: false } : { key, asc: true });

	return arr.sort((a, b) => {
		for (const f of fields) {
			if (a[f.key] < b[f.key])
				return f.asc ? -1 : 1;

			if (a[f.key] > b[f.key])
				return f.asc ? 1 : -1;
		}

		return 0;
	});
};

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export function formdata(obj, data) {
	const fd = new FormData();

	if (typeof data !== "undefined")
		fd.set("_json", JSON.stringify(data));

	for (const [key, value] of Object.entries(obj))
		if (typeof value !== "undefined")
			fd.set(key, value);

	return fd;
}

export function validateEmail(email) {
	const regex = /^[a-zA-Z0-9\.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

	return regex.test(email);
}

export function formatAddress(loc) {
	return ["addr", "addr2", "city", "county"].filter((key) => loc[key]).map((key) => loc[key]).join(", ");
}

export function randHexStr(length) {
	return Array.from(crypto.getRandomValues(new Uint8Array(length))).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const perms = {};

Object.defineProperty(perms, "ADMIN", { value: 2, writable: false });
Object.defineProperty(perms, "ROOT", { value: 128, writable: false });
